import { useCallback, useEffect, useMemo } from 'react';

import { type Constraint } from '../types';
import { useStore } from '../motionEditorStore';

export default function TimelineBar() {
  const store = useStore();
  const snap = store.state;

  // ── Keyboard shortcuts (global) ───────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Ignore if user is typing in an input / textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!store.state.bvhText) return;

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          store.togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          store.setPlaying(false);
          store.setFrame(store.state.currentFrame - (e.shiftKey ? 10 : 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          store.setPlaying(false);
          store.setFrame(store.state.currentFrame + (e.shiftKey ? 10 : 1));
          break;
        case 'Home':
          e.preventDefault();
          store.setPlaying(false);
          store.setFrame(0);
          break;
        case 'End':
          e.preventDefault();
          store.setPlaying(false);
          store.setFrame(store.state.totalFrames - 1);
          break;
      }
    },
    [store],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Compute unique keyframe positions as percentages for the tick marks
  const keyframePercents = useMemo(() => {
    const constraints = snap.constraints as Constraint[];
    if (!constraints.length || snap.totalFrames <= 1) return [];
    const uniqueFrames = [...new Set(constraints.map((c) => c.frame))];
    return uniqueFrames.map((f) => (f / (snap.totalFrames - 1)) * 100);
  }, [snap.constraints, snap.totalFrames]);

  if (!snap.bvhText) return null;

  const progress =
    snap.totalFrames > 1
      ? (snap.currentFrame / (snap.totalFrames - 1)) * 100
      : 0;

  return (
    <div className="pointer-events-auto absolute bottom-0 left-1/2 flex w-[min(60%,36rem)] -translate-x-1/2 flex-col gap-0 rounded-t-lg bg-gradient-to-t from-black/80 to-black/40 px-4 pt-5 pb-3 backdrop-blur-sm">
      {/* Scrubber track */}
      <div className="group relative mb-2 h-1.5 w-full cursor-pointer rounded-full bg-white/20">
        {/* Keyframe tick marks */}
        {keyframePercents.map((pct) => (
          <div
            key={pct}
            className="absolute top-1/2 h-3 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-yellow-400/80"
            style={{ left: `${pct}%` }}
          />
        ))}
        {/* Progress fill */}
        <div
          className="bg-blue absolute top-0 left-0 h-full rounded-full transition-[width] duration-75"
          style={{ width: `${progress}%` }}
        />
        {/* Native range input overlaid for interaction */}
        <input
          type="range"
          min={0}
          max={snap.totalFrames - 1}
          value={snap.currentFrame}
          onChange={(e) => {
            store.setPlaying(false);
            store.setFrame(parseInt(e.target.value));
          }}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
        {/* Thumb indicator */}
        <div
          className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow transition-[left] duration-75 group-hover:h-4 group-hover:w-4"
          style={{ left: `${progress}%` }}
        />
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-3">
        {/* Transport buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => store.setFrame(0)}
            className="rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            title="Go to start (Home)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
            </svg>
          </button>

          <button
            onClick={() => {
              store.setPlaying(false);
              store.setFrame(snap.currentFrame - 1);
            }}
            className="rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            title="Previous frame (←)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path
                d="M6 6h2v12H6zm3.5 6l8.5 6V6z"
                transform="scale(-1,1) translate(-24,0)"
              />
            </svg>
          </button>

          <button
            onClick={() => store.togglePlay()}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
            title="Play / Pause (Space)"
          >
            {snap.isPlaying ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            onClick={() => {
              store.setPlaying(false);
              store.setFrame(snap.currentFrame + 1);
            }}
            className="rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            title="Next frame (→)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>

          <button
            onClick={() => store.setFrame(snap.totalFrames - 1)}
            className="rounded p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
            title="Go to end (End)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>
        </div>

        {/* Frame counter */}
        <span className="min-w-[7rem] font-mono text-xs text-white/80 tabular-nums">
          {snap.currentFrame} / {snap.totalFrames - 1} &middot; {snap.frameRate}
          fps
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Speed selector */}
        <div className="flex items-center gap-1">
          {[0.25, 0.5, 1, 2].map((s) => (
            <button
              key={s}
              onClick={() => store.setPlaybackSpeed(s)}
              className={`rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors ${
                snap.playbackSpeed === s
                  ? 'bg-blue text-white'
                  : 'bg-white/10 text-white/60 hover:bg-white/20 hover:text-white'
              }`}
            >
              {s}x
            </button>
          ))}
        </div>

        {/* Keyboard hint */}
        <span className="hidden text-[10px] text-white/40 sm:inline">
          Space: play &middot; ←→: step &middot; Shift+←→: ×10
        </span>
      </div>
    </div>
  );
}
